import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { tenderAPI } from '../utils/api';
import { ArrowLeft, Loader2, ExternalLink, Check, X, Minus } from 'lucide-react';

function TenderCompare() {
  var searchParams = useSearchParams()[0];
  var navigate = useNavigate();
  var ids = (searchParams.get('ids') || '').split(',').filter(Boolean);
  var s1 = useState([]); var tenders = s1[0]; var setTenders = s1[1];
  var s2 = useState(true); var loading = s2[0]; var setLoading = s2[1];

  useEffect(function() {
    if (ids.length < 2) return;
    var load = async function() {
      try {
        var results = await Promise.all(ids.map(function(id) { return tenderAPI.get(id); }));
        setTenders(results.map(function(r) { return r.data; }));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (ids.length < 2) {
    return (
      <div className="p-6 text-center py-16">
        <p className="text-slate-500">Select at least 2 tenders to compare.</p>
        <button onClick={function() { navigate('/tender-scout/tenders'); }} className="mt-4 text-primary underline text-sm">Go to Tender List</button>
      </div>
    );
  }

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-primary" size={28} /></div>;

  var getScoreColor = function(score) {
    if (score >= 4) return 'text-green-600 bg-green-50';
    if (score >= 3) return 'text-amber-600 bg-amber-50';
    return 'text-slate-600 bg-slate-50';
  };

  var bestScore = Math.max.apply(null, tenders.map(function(t) { return t.final_score || 0; }));

  var formatDate = function(d) {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  var formatValue = function(v) {
    if (!v) return '-';
    return 'RM ' + Number(v).toLocaleString();
  };

  var fields = [
    { label: 'Score', key: 'final_score', render: function(t) { var cls = getScoreColor(t.final_score || 0); var isBest = t.final_score === bestScore && bestScore > 0; return <div className="flex items-center gap-2"><span className={'text-lg font-bold px-2.5 py-1 rounded-lg ' + cls}>{t.final_score || 0}/5</span>{isBest && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Best</span>}</div>; } },
    { label: 'Status', key: 'status', render: function(t) { return <span className="text-sm capitalize px-2 py-1 rounded bg-slate-100 text-slate-700">{t.status}</span>; } },
    { label: 'Organisation', key: 'organisation', render: function(t) { return <span className="text-sm text-slate-700">{t.organisation || '-'}</span>; } },
    { label: 'Reference', key: 'reference', render: function(t) { return <span className="text-sm text-slate-500 font-mono">{t.reference || '-'}</span>; } },
    { label: 'Source', key: 'source_name', render: function(t) { return <span className="text-sm text-slate-600">{t.source_name || '-'}</span>; } },
    { label: 'Closing Date', key: 'closing_date', render: function(t) { var d = formatDate(t.closing_date); var isPast = t.closing_date && new Date(t.closing_date) < new Date(); return <span className={'text-sm ' + (isPast ? 'text-red-500' : 'text-slate-700')}>{d} {isPast ? '(Expired)' : ''}</span>; } },
    { label: 'Estimated Value', key: 'estimated_value_myr', render: function(t) { return <span className="text-sm font-medium text-slate-900">{formatValue(t.estimated_value_myr)}</span>; } },
    { label: 'Category', key: 'category', render: function(t) { return <span className="text-sm text-slate-600">{t.category || '-'}</span>; } },
    { label: 'Matched Keywords', key: 'matched_keywords', render: function(t) {
      var kw = t.matched_keywords || [];
      if (kw.length === 0) return <span className="text-xs text-slate-400">None</span>;
      return <div className="flex flex-wrap gap-1">{kw.map(function(k, i) { return <span key={i} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">{k}</span>; })}</div>;
    }},
    { label: 'URL', key: 'url', render: function(t) {
      if (!t.url) return <span className="text-xs text-slate-400">-</span>;
      return <a href={t.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1" onClick={function(e) { e.stopPropagation(); }}><ExternalLink size={12} /> View Tender</a>;
    }},
  ];

  return (
    <div className="p-3 sm:p-6 space-y-5" data-testid="tender-compare-page">
      <div className="flex items-center gap-3 sm:gap-4">
        <button onClick={function() { navigate('/tender-scout/tenders'); }} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-to-list-btn">
          <ArrowLeft size={20} className="text-slate-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Compare Tenders</h1>
          <p className="text-sm text-slate-500">Side-by-side comparison of {tenders.length} tenders</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full">
          {/* Header row with tender titles */}
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="py-4 px-5 text-left text-xs font-medium text-slate-500 w-40 min-w-[160px]">Field</th>
              {tenders.map(function(t) {
                var isBest = t.final_score === bestScore && bestScore > 0;
                return (
                  <th key={t._id} className={'py-4 px-5 text-left min-w-[260px] ' + (isBest ? 'bg-green-50/50' : '')} data-testid={'compare-col-' + t._id}>
                    <p className="text-sm font-semibold text-slate-900 line-clamp-2 leading-tight">{t.title}</p>
                    <button onClick={function() { navigate('/tender-scout/tenders/' + t._id); }} className="text-xs text-primary hover:underline mt-1">View Detail</button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {fields.map(function(field) {
              return (
                <tr key={field.key} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="py-3.5 px-5 text-xs font-medium text-slate-500 bg-slate-50/50">{field.label}</td>
                  {tenders.map(function(t) {
                    var isBest = t.final_score === bestScore && bestScore > 0;
                    return (
                      <td key={t._id} className={'py-3.5 px-5 ' + (isBest && field.key === 'final_score' ? 'bg-green-50/30' : '')}>
                        {field.render(t)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-3">
        {tenders.map(function(t) {
          return (
            <button key={t._id} onClick={function() { navigate('/tender-scout/tenders/' + t._id); }} className="flex-1 px-4 py-3 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-left" data-testid={'compare-action-' + t._id}>
              <p className="text-sm font-medium text-slate-900 truncate">{t.title}</p>
              <p className="text-xs text-primary mt-1">View Full Detail &rarr;</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default TenderCompare;
